/**
 * PollViewer - Visualizador e votação de enquetes (para todos)
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { BarChart3, Check, Users } from 'lucide-react';
import { pollApi } from '../services/pollApi';
import type { PollState, PollSyncMessage, PollStartData, PollVoteUpdateData, PollCloseData, PollOptionResult } from '../types';

interface PollViewerProps {
    tableId: string;
    isMaster?: boolean;
    userId?: string;
    lastMessage?: PollSyncMessage;
}

interface LocalPollState {
    isActive: boolean;
    pollId?: string;
    question?: string;
    options?: Array<{ id: string; text: string; order: number }>;
    pollType?: 'single' | 'multiple';
    isAnonymous?: boolean;
    showResultsLive?: boolean;
    hasVoted: boolean;
    votedOptionIds: string[];
    results?: {
        options: Array<{ option_id: string; votes: number; percentage: number }>;
        totalVoters: number;
        totalVotes: number;
    };
}

export function PollViewer({ tableId, isMaster = false, userId, lastMessage }: PollViewerProps) {
    const [state, setState] = useState<LocalPollState>({
        isActive: false,
        hasVoted: false,
        votedOptionIds: []
    });
    const [selectedOption, setSelectedOption] = useState<string>('');
    const [isVoting, setIsVoting] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // Carregar estado inicial
    useEffect(() => {
        const loadState = async () => {
            try {
                const pollState = await pollApi.getActivePoll(tableId);
                console.log('[POLL] Estado inicial:', pollState);
                
                if (pollState.is_active && pollState.poll_id) {
                    setState({
                        isActive: true,
                        pollId: pollState.poll_id,
                        question: pollState.question,
                        options: pollState.options?.map(opt => ({
                            id: opt.id,
                            text: opt.text,
                            order: opt.order
                        })),
                        pollType: pollState.poll_type,
                        isAnonymous: pollState.is_anonymous,
                        showResultsLive: pollState.show_results_live,
                        hasVoted: pollState.has_voted || false,
                        votedOptionIds: pollState.voted_option_ids || [],
                        results: pollState.results ? {
                            options: pollState.results.results.map(r => ({
                                option_id: r.id,
                                votes: r.votes,
                                percentage: r.percentage
                            })),
                            totalVoters: pollState.results.total_voters,
                            totalVotes: pollState.results.total_votes
                        } : undefined
                    });
                    
                    // Pré-selecionar opção votada
                    if (pollState.voted_option_ids && pollState.voted_option_ids.length > 0) {
                        setSelectedOption(pollState.voted_option_ids[0]);
                    }
                }
            } catch (error) {
                console.error('[POLL] Erro ao carregar estado:', error);
            }
        };
        
        if (tableId) loadState();
    }, [tableId]);

    // Ouvir atualizações via WebSocket
    useEffect(() => {
        if (!lastMessage) return;

        console.log('[POLL] Processando mensagem:', lastMessage);

        if (lastMessage.action === 'start') {
            const data = lastMessage.data as PollStartData;
            
            // Verificar se é para esta mesa
            if (data.table_id !== tableId) {
                console.log('[POLL] Ignorando mensagem de outra mesa');
                return;
            }
            
            setState({
                isActive: true,
                pollId: data.poll_id,
                question: data.question,
                options: data.options,
                pollType: data.poll_type,
                isAnonymous: data.is_anonymous,
                showResultsLive: data.show_results_live,
                hasVoted: false,
                votedOptionIds: [],
                results: undefined
            });
            setSelectedOption('');
            toast.info('Nova enquete iniciada!');
            
        } else if (lastMessage.action === 'vote_update') {
            const data = lastMessage.data as PollVoteUpdateData;
            
            if (data.poll_id !== state.pollId) return;
            
            setState(prev => ({
                ...prev,
                results: {
                    options: data.results,
                    totalVoters: data.total_voters,
                    totalVotes: data.total_votes
                }
            }));
            
        } else if (lastMessage.action === 'close') {
            const data = lastMessage.data as PollCloseData;
            
            if (data.table_id !== tableId) return;
            
            setState(prev => ({
                ...prev,
                isActive: false
            }));
            toast.info('Enquete encerrada!');
        }
    }, [lastMessage, tableId, state.pollId]);

    const handleVote = async () => {
        if (!selectedOption || !state.pollId) {
            toast.error('Selecione uma opção');
            return;
        }

        setIsVoting(true);
        try {
            const response = await pollApi.vote(state.pollId, {
                option_ids: [selectedOption]
            });
            
            setState(prev => ({
                ...prev,
                hasVoted: true,
                votedOptionIds: [selectedOption],
                results: response.results ? {
                    options: response.results.results.map(r => ({
                        option_id: r.id,
                        votes: r.votes,
                        percentage: r.percentage
                    })),
                    totalVoters: response.results.total_voters,
                    totalVotes: response.results.total_votes
                } : prev.results
            }));
            
            toast.success('Voto registrado!');
        } catch (error: any) {
            console.error('[POLL] Erro ao votar:', error);
            toast.error(error.message || 'Erro ao registrar voto');
        } finally {
            setIsVoting(false);
        }
    };

    const handleClose = async () => {
        if (!state.pollId) return;

        setIsClosing(true);
        try {
            await pollApi.closePoll(state.pollId);
            // O estado será atualizado pelo WebSocket
        } catch (error: any) {
            console.error('[POLL] Erro ao encerrar:', error);
            toast.error(error.message || 'Erro ao encerrar enquete');
        } finally {
            setIsClosing(false);
        }
    };

    const getOptionResult = (optionId: string) => {
        if (!state.results) return null;
        return state.results.options.find(r => r.option_id === optionId);
    };

    const showResults = state.hasVoted || state.showResultsLive || isMaster;

    if (!state.isActive) return null;

    return (
        <Dialog open={state.isActive} onOpenChange={() => {}}>
            <DialogContent
                className="bg-game-secondary border-game-primary max-w-lg [&>button:last-child]:hidden"
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="text-game-primary flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-violet-400" />
                        Enquete
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Pergunta */}
                    <div className="text-lg font-medium text-game-primary">
                        {state.question}
                    </div>

                    {/* Opções */}
                    <div className="space-y-3">
                        {!state.hasVoted ? (
                            <RadioGroup
                                value={selectedOption}
                                onValueChange={setSelectedOption}
                                className="space-y-2"
                            >
                                {state.options?.sort((a, b) => a.order - b.order).map((option) => {
                                    const result = getOptionResult(option.id);
                                    
                                    return (
                                        <div key={option.id} className="relative">
                                            <div className="flex items-center space-x-3 p-3 rounded-lg bg-game-dark border border-game-hover hover:border-violet-500/50 transition-colors">
                                                <RadioGroupItem 
                                                    value={option.id} 
                                                    id={option.id}
                                                    className="border-game-hover text-violet-500"
                                                />
                                                <Label 
                                                    htmlFor={option.id} 
                                                    className="flex-1 cursor-pointer text-game-primary"
                                                >
                                                    {option.text}
                                                </Label>
                                                {showResults && result && (
                                                    <span className="text-sm text-game-muted">
                                                        {result.percentage}%
                                                    </span>
                                                )}
                                            </div>
                                            {showResults && result && (
                                                <Progress 
                                                    value={result.percentage} 
                                                    className="h-1 absolute bottom-0 left-0 right-0 rounded-b-lg"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </RadioGroup>
                        ) : (
                            <div className="space-y-2">
                                {state.options?.sort((a, b) => a.order - b.order).map((option) => {
                                    const result = getOptionResult(option.id);
                                    const isVoted = state.votedOptionIds.includes(option.id);
                                    const isWinner = result && state.results && 
                                        result.votes === Math.max(...state.results.options.map(r => r.votes)) &&
                                        result.votes > 0;
                                    
                                    return (
                                        <div 
                                            key={option.id} 
                                            className={`relative p-3 rounded-lg bg-game-dark border transition-colors ${
                                                isVoted ? 'border-violet-500' : 'border-game-hover'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    {isWinner && (
                                                        <span className="text-yellow-500">🏆</span>
                                                    )}
                                                    <span className={`${isVoted ? 'text-violet-400' : 'text-game-primary'}`}>
                                                        {option.text}
                                                    </span>
                                                    {isVoted && (
                                                        <Check className="h-4 w-4 text-violet-400" />
                                                    )}
                                                </div>
                                                <span className="text-sm font-medium text-game-muted">
                                                    {result?.percentage || 0}% ({result?.votes || 0})
                                                </span>
                                            </div>
                                            <Progress 
                                                value={result?.percentage || 0} 
                                                className="h-2"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Contador de votos */}
                    {showResults && state.results && (
                        <div className="flex items-center justify-center gap-2 text-sm text-game-muted">
                            <Users className="h-4 w-4" />
                            <span>{state.results.totalVoters} participante{state.results.totalVoters !== 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>

                {/* Ações */}
                <div className="flex justify-end gap-2">
                    {!state.hasVoted && (
                        <Button
                            onClick={handleVote}
                            disabled={!selectedOption || isVoting}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                        >
                            {isVoting ? 'Votando...' : 'Votar'}
                        </Button>
                    )}
                    
                    {isMaster && (
                        <Button
                            variant="destructive"
                            onClick={handleClose}
                            disabled={isClosing}
                        >
                            {isClosing ? 'Encerrando...' : 'Encerrar Enquete'}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
